import React from "react";
import {
  LegalLayout,
  LegalH2,
  LegalH3,
  LegalP,
  LegalUl
} from "../../components/landing/LegalLayout";

const Mail = () => (
  <a
    href="mailto:wasgitshuet.support@liberox.ch"
    className="font-semibold text-terracotta hover:underline"
  >
    wasgitshuet.support@liberox.ch
  </a>
);

export default function DatenschutzEn() {
  return (
    <LegalLayout
      testId="page-datenschutz"
      title="Privacy Policy"
      subtitle="Privacy policy for the «WasGitsHüt?» app under the Swiss Data Protection Act (FADP) and the Data Protection Ordinance (DPO) and – where applicable – the EU General Data Protection Regulation (GDPR)."
    >
      <LegalP>Last updated: 18 September 2026</LegalP>
      <LegalP>
        This privacy policy explains which personal data we process when you use
        the «WasGitsHüt?» app and the associated website, why we do so and which
        rights you have. We are primarily governed by the Swiss Data Protection
        Act (FADP) and the Data Protection Ordinance (DPO). For users within the
        European Union we additionally take the EU General Data Protection
        Regulation (GDPR) into account.
      </LegalP>
      <LegalP>
        As a matter of principle we process only as much data as necessary (data
        minimisation) and design our processes according to the principles of
        «privacy by design» and «privacy by default». Wherever possible we
        distinguish between data that is technically necessary, information you
        provide voluntarily and data required for specific features.
      </LegalP>

      <LegalH2>1. Data controller</LegalH2>
      <LegalP>The controller responsible for processing your personal data is:</LegalP>
      <LegalUl
        items={[
          "Liberox GmbH («WasGitsHüt?» is a brand of Liberox GmbH)",
          "Bodenäckerstrasse 3, 8957 Spreitenbach, Switzerland",
          "Company ID (UID): CHE-265.395.466",
          "Phone: 056 561 67 62"
        ]}
      />
      <LegalP>
        E-mail for privacy matters: <Mail />
      </LegalP>

      <LegalH2>2. Scope</LegalH2>
      <LegalP>
        This privacy policy applies to the «WasGitsHüt?» app (for iOS and
        Android) and to our website. Swiss law (FADP/DPO) applies in the first
        instance. Where the GDPR applies to you (e.g. because you are located in
        the EU), the additional provisions of this policy that expressly refer to
        the GDPR apply.
      </LegalP>

      <LegalH2>3. Which personal data we process</LegalH2>
      <LegalP>
        Depending on how you use the app, we may process the following categories
        of personal data:
      </LegalP>
      <LegalH3>a) Technically necessary data</LegalH3>
      <LegalUl
        items={[
          "Account and login data (e.g. e-mail address or an identifier from the Apple or Google login)",
          "We process technical information such as device type, operating system, app version, IP address, time of access and technical identifiers, insofar as this is necessary for the operation, security and troubleshooting of the app."
        ]}
      />
      <LegalH3>b) Voluntary profile information</LegalH3>
      <LegalUl
        items={[
          "Display name and profile details",
          "Personal food preferences as well as favourite cuisines and dishes",
          "Diets (e.g. vegetarian, vegan)",
          "Food dislikes and likes",
          "Budget preferences for dishes (in CHF)"
        ]}
      />
      <LegalH3>c) Particularly sensitive information</LegalH3>
      <LegalUl items={["Allergies and intolerances (see chapter 8)"]} />
      <LegalH3>d) Feature-related usage data</LegalH3>
      <LegalUl
        items={[
          "Households and household members as well as invitations",
          "Votes on suggested dishes",
          "Favourited and saved recipes",
          "Shopping lists",
          "Information related to subscriptions and in-app purchases (see chapter 11)"
        ]}
      />

      <LegalH2>4. User account and registration</LegalH2>
      <LegalP>
        To use the core features you create a user account. In doing so we
        process the data required for sign-in (e.g. e-mail address or an
        identifier from the Apple or Google login). The legal basis is the
        performance of the usage contract; under the GDPR this is based on
        Art. 6(1)(b).
      </LegalP>

      <LegalH2>5. Apple login</LegalH2>
      <LegalP>
        If you register via «Sign in with Apple», we receive from Apple the
        information required to create your account (e.g. an identifier and – if
        released by you – an e-mail address, where applicable via an anonymised
        relay address from Apple). Sign-in takes place in accordance with Apple's
        rules and privacy policy.
      </LegalP>

      <LegalH2>6. Google login</LegalH2>
      <LegalP>
        When registering via «Sign in with Google», we receive from Google the
        information required to create your account (e.g. an identifier and
        e-mail address). Sign-in takes place in accordance with Google's rules
        and privacy policy.
      </LegalP>

      <LegalH2>7. Households and household members</LegalH2>
      <LegalP>
        With «WasGitsHüt?» you can create a household and invite and manage
        additional people as members. Within a household, certain information is
        shared with the other members – in particular shared recipes, votes and
        shopping lists. Your personal food preferences, however, are visible only
        to you and to the administration of your account and are not shared with
        other household members. Since several people can be members of the same
        household, you should only share information intended for the other
        members. You can leave a household at any time.
      </LegalP>

      <LegalH2>8. Allergies, intolerances and dietary preferences</LegalH2>
      <LegalP>
        You can voluntarily store allergies, intolerances and dietary information
        so that more suitable recipes can be suggested to you. Information about
        allergies and intolerances may allow conclusions to be drawn about your
        health. Such data may qualify as «particularly sensitive personal data»
        under the Swiss FADP and as «special categories of personal data» (health
        data, Art. 9 GDPR) under the GDPR.
      </LegalP>
      <LegalP>
        We process this information solely on the basis of your explicit consent,
        which you give by entering it in your profile, and only for the purpose
        of finding recipes. You can withdraw this consent at any time and delete
        the information. Providing this data is voluntary; without it, the other
        features remain available to you.
      </LegalP>

      <LegalH2>9. Recipe generation and artificial intelligence</LegalH2>
      <LegalP>
        To create recipe suggestions as well as recipe texts and recipe images we
        use features based on artificial intelligence. The inputs required for
        the suggestion (e.g. available ingredients, desired cuisine, dietary
        information) are transmitted to external technology providers (see
        chapter 16). Only the information required to produce the desired result
        is transferred.
      </LegalP>

      <LegalH2>10. Storage of recipes, favourites and shopping lists</LegalH2>
      <LegalP>
        So that you can find your results again, we store favourited recipes,
        voting results and shopping lists you have created. Within a household
        this content can be shared with the other members. The legal basis is the
        performance of the usage contract (under the GDPR, Art. 6(1)(b)).
      </LegalP>

      <LegalH2>11. Subscriptions and payments</LegalH2>
      <LegalP>
        «WasGitsHüt?» may offer paid subscriptions and in-app purchases. Payment
        is processed via the Apple App Store or Google Play. We do not receive or
        store credit card or full payment data. We only receive the information
        required to manage the subscription (e.g. subscription status, a
        transaction or order identifier). Any prices are stated in CHF.
      </LegalP>

      <LegalH2>12. RevenueCat</LegalH2>
      <LegalP>
        To manage subscriptions and in-app purchases we may use the RevenueCat
        service. RevenueCat processes information relating to purchases and
        subscriptions on our behalf (e.g. purchase and subscription status as
        well as store-assigned identifiers). Processing takes place in accordance
        with RevenueCat's privacy policy.
      </LegalP>

      <LegalH2>13. Supabase</LegalH2>
      <LegalP>
        For backend, database, authentication and other backend functions we may
        use the Supabase service. Supabase processes the data stored in the app
        on our behalf as a processor.
      </LegalP>

      <LegalH2>14. Apple App Store</LegalH2>
      <LegalP>
        If you obtain the app via the Apple App Store and make purchases there,
        Apple processes the associated data (e.g. purchase and payment handling)
        as an independent controller in accordance with Apple's privacy policy.
      </LegalP>

      <LegalH2>15. Google Play Store</LegalH2>
      <LegalP>
        If you obtain the app via Google Play and make purchases there, Google
        processes the associated data (e.g. purchase and payment handling) as an
        independent controller in accordance with Google's privacy policy.
      </LegalP>

      <LegalH2>16. Use of artificial intelligence</LegalH2>
      <LegalP>
        For the generation of recipe texts and recipe images as well as for
        individual features such as speech processing, we use specialised
        external technology providers. Only the inputs required for the
        respective feature are transmitted to them. We choose providers that
        ensure an appropriate level of data protection and do not transmit more
        information than necessary. For competitive and confidentiality reasons we
        do not name the specific providers and models.
      </LegalP>

      <LegalH2>17. Disclosure of data to third parties and processors</LegalH2>
      <LegalP>
        We disclose personal data only to the extent necessary for the purposes
        described. Our backend and technology providers in particular may act as
        processors. Apple and Google act as independent controllers in the
        context of login, store and payments. We do not sell your personal data
        and do not pass it on to third parties for advertising purposes.
      </LegalP>

      <LegalH2>18. Processing and storage of data abroad</LegalH2>
      <LegalP>
        The data collected in the app is stored on servers in Frankfurt (Germany)
        and thus within the EU. Germany provides an adequate level of data
        protection; processing takes place in accordance with the Swiss FADP and
        the EU GDPR. Insofar as individual providers (e.g. for login, stores or
        specific features) process data in other countries, we rely on
        appropriate legal safeguards such as standard contractual clauses.
      </LegalP>

      <LegalH2>19. Data security</LegalH2>
      <LegalP>
        We take appropriate technical and organisational measures to protect your
        data – in particular encryption in transit, access restrictions and the
        separation of household data. For data storage we rely on a certified
        database and hosting provider (MongoDB Atlas), which is certified in
        accordance with the following standards, among others:
      </LegalP>
      <LegalUl
        items={[
          "ISO/IEC 27001:2022 (information security management system)",
          "ISO/IEC 27017:2015 (information security for cloud services)",
          "ISO/IEC 27018:2019 (protection of personal data in the cloud)",
          "ISO 9001:2015 (quality management)",
          "SOC 2 Type II"
        ]}
      />

      <LegalH2>20. Retention period and deletion of data</LegalH2>
      <LegalP>
        We process personal data only for as long as is necessary for the
        respective purpose or as required by statutory retention obligations.
      </LegalP>

      <LegalH2>21. Deletion of a user account</LegalH2>
      <LegalP>
        You can request the deletion of your user account and the associated data
        at any time. After deletion, your personal information will be removed
        unless statutory retention obligations prevent this. Content already
        shared with a household may remain with the other members in anonymised
        form or form detached from you. Deleting an account does not automatically
        terminate an ongoing store subscription – you must cancel this via Apple
        or Google. You request deletion by e-mail (deletion directly within the
        app is not provided):
      </LegalP>
      <LegalUl items={[<>By e-mail to: <Mail /></>]} />

      <LegalH2>22. Rights of data subjects under the Swiss FADP</LegalH2>
      <LegalP>Under the Swiss FADP you have in particular the following rights:</LegalP>
      <LegalUl
        items={[
          "Right to information about the data processed about you",
          "Right to rectification of inaccurate data",
          "Right to deletion or destruction of data",
          "Right to the release or transfer of your data",
          "Right to object to a specific processing of data"
        ]}
      />
      <LegalP>
        You can request the deletion of your data and your account by e-mail to{" "}
        <Mail />.
      </LegalP>

      <LegalH2>23. Additional rights for persons to whom the GDPR applies</LegalH2>
      <LegalP>If you fall under the GDPR, you additionally have the following rights:</LegalP>
      <LegalUl
        items={[
          "Right to restriction of processing",
          "Right to data portability in a common format",
          "Right to object to certain processing",
          "Right to withdraw a given consent (with effect for the future)",
          "Right to lodge a complaint with a data protection supervisory authority"
        ]}
      />
      <LegalP>
        To exercise your rights, contact us at <Mail />. In Switzerland the
        Federal Data Protection and Information Commissioner (FDPIC) is
        responsible; in the EU, the respective national supervisory authority.
      </LegalP>

      <LegalH2>24. Minors and use within a family household</LegalH2>
      <LegalP>
        «WasGitsHüt?» is used in a family environment. An age of the using person
        is neither required nor collected or requested for use – this applies
        equally to children and adults. If children are added as household
        members, this is done by a parent or legal guardian; the consent required
        for this is given when that person adds the child themselves. Data of
        children is not used for advertising or profiling purposes.
      </LegalP>

      <LegalH2>25. Cookies and website technologies</LegalH2>
      <LegalP>
        On our website, technically necessary technologies as well as – with your
        consent – further technologies (e.g. for statistics) may be used.
      </LegalP>

      <LegalH2>26. Changes to this privacy policy</LegalH2>
      <LegalP>
        We may adjust this privacy policy to reflect changed processes or legal
        requirements. The version published on this page is authoritative. In the
        event of material changes we will inform you in an appropriate manner.
      </LegalP>

      <LegalH2>27. Contact</LegalH2>
      <LegalP>For privacy questions you can reach us at:</LegalP>
      <LegalUl
        items={[
          "Liberox GmbH, Bodenäckerstrasse 3, 8957 Spreitenbach, Switzerland",
          "Phone: 056 561 67 62",
          <>E-mail: <Mail /></>
        ]}
      />
    </LegalLayout>
  );
}
